import { useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Building2, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const MAX_DISPLAY_ROWS = 50;

export function ManagementTypeBackfillPanel() {
  const [loadingCensus, setLoadingCensus] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [census, setCensus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState(null);

  const [loadingContradiction, setLoadingContradiction] = useState(false);
  const [contradiction, setContradiction] = useState(null);
  const [savingRowId, setSavingRowId] = useState(null);

  const [loadingBlindness, setLoadingBlindness] = useState(false);
  const [blindness, setBlindness] = useState(null);
  const ruledDirectCount = blindness ? blindness.rows.filter((r) => r.label === 'direct (ruled)').length : 0;

  const runBlindnessTally = async () => {
    setLoadingBlindness(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.entityBlindnessTally();
      setBlindness(data);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingBlindness(false);
    }
  };

  const runContradictionCensus = async () => {
    setLoadingContradiction(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.managementTypeContradictionCensus();
      setContradiction(data);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingContradiction(false);
    }
  };

  const flipToManaged = async (row, feeRate) => {
    setSavingRowId(row.canonical_property_id);
    try {
      await diagnosticsAPI.setPropertyManagementType({
        platform: row.primary_platform,
        propertyId: row.primary_property_id,
        managementType: 'managed',
        managementFeeRate: feeRate,
      });
      toast.success(`${row.property_name} marked managed. Re-run the restamp census above to see its bookings.`);
      // Refresh both cards so the wiring is visible immediately: the row
      // drops out of the contradiction list, and the restamp backlog above
      // picks up the property's (still-unrestamped) bookings.
      await Promise.all([runContradictionCensus(), runCensus()]);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSavingRowId(null);
    }
  };

  const runCensus = async () => {
    setLoadingCensus(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.managementTypeBackfillCensus();
      setCensus(data);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingCensus(false);
    }
  };

  const runPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.managementTypeBackfillPreview();
      setPreview(data);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingPreview(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    setConfirmApply(false);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.managementTypeBackfillApply();
      setApplyResult(data);
      if (data.total.restamped > 0) {
        toast.success(`Restamped ${data.total.restamped} booking(s) to management_type=managed.`);
      } else {
        toast.info('Nothing to restamp -- backlog already clear.');
      }
      // Refresh census/preview so the card reflects the new state immediately.
      runCensus();
      setPreview(null);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card data-testid="management-type-backfill-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Building2 className="h-5 w-5 text-blue-500" />
          management_type Restamp Backfill
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          4bb8767e fixed uplisting/smoobu/hostaway sync to stop reclassifying a
          managed-no-fee-rate property's bookings as "owned". Bookings synced
          BEFORE that fix keep the wrong stamp until they happen to resync
          (Smoobu/Hostaway only resync a rolling 90-day window). This backlog
          still counts as a direct receivable ("Unpaid bookings") on Assets &amp;
          Liabilities for the affected entities instead of routing through
          "Unpaid mgmt statements". Census is read-only. Apply restamps
          bookings whose property is currently managed without touching
          amount fields it can&apos;t safely recompute; for bookings whose
          property now has a fee rate configured ("needs_resync"), it still
          flips management_type (stamp-only) and marks needs_resync=true so
          they stop being misclassified while the real commission awaits a
          proper resync.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button onClick={runCensus} disabled={loadingCensus} data-testid="mgmt-type-backfill-census-btn">
            {loadingCensus ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Run Census
          </Button>
          <Button onClick={runPreview} disabled={loadingPreview} variant="outline" data-testid="mgmt-type-backfill-preview-btn">
            {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          {!confirmApply ? (
            <Button variant="destructive" onClick={() => setConfirmApply(true)} disabled={applying} data-testid="mgmt-type-backfill-apply-btn">
              Apply
            </Button>
          ) : (
            <Button variant="destructive" onClick={runApply} disabled={applying} data-testid="mgmt-type-backfill-confirm-apply-btn">
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
              Confirm Apply
            </Button>
          )}
        </div>

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3">
            Error: {error}
          </div>
        )}

        {census && (
          <div className="space-y-2" data-testid="mgmt-type-backfill-census-results">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Total mismatched</p>
                <p className="text-xl font-bold tabular-nums">{census.total_mismatched}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">By platform</p>
                <p className="text-xs font-mono">
                  {Object.entries(census.platform_totals).map(([p, c]) => `${p}: ${c}`).join(', ')}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Reverse anomaly (owned→managed)</p>
                <p className="text-xl font-bold tabular-nums text-amber-600 dark:text-amber-400">
                  {Object.values(census.reverse_anomaly_counts).reduce((a, b) => a + b, 0)}
                </p>
              </div>
            </div>

            {census.total_mismatched === 0 ? (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                No managed-property bookings are stamped incorrectly.
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-2 font-medium">Entity</th>
                      <th className="text-left px-3 py-2 font-medium">Platform</th>
                      <th className="text-right px-3 py-2 font-medium">Count</th>
                      <th className="text-right px-3 py-2 font-medium">Cancelled</th>
                      <th className="text-right px-3 py-2 font-medium">Needs resync</th>
                    </tr>
                  </thead>
                  <tbody>
                    {census.rows.map((r, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-3 py-2">{r.entity_name}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{r.platform}</Badge></td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.count}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.cancelled_count}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-600 dark:text-amber-400">{r.needs_resync_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {Object.values(census.reverse_anomaly_counts).some((c) => c > 0) && (
              <p className="text-xs text-muted-foreground italic">{census.reverse_anomaly_note}</p>
            )}
          </div>
        )}

        {preview && (
          <div className="space-y-1" data-testid="mgmt-type-backfill-preview-results">
            <p className="text-sm font-medium">
              Preview: {preview.summary.restamp || 0} would restamp, {preview.summary.needs_resync || 0} need a real resync,{' '}
              {preview.summary.stamp_restamp || 0} would stamp-only restamp
            </p>
            {preview.summary.stamp_restamp > 0 && (
              <p className="text-xs text-muted-foreground italic">
                Stamp-only restamp: management_type is flipped to managed and needs_resync is marked
                True so Receivables stops treating these as a direct "Unpaid bookings" receivable, but
                commission_type/management_fee are left untouched until the property's next real sync
                computes the actual commission.
              </p>
            )}
            {preview.stamp_restamp_samples?.length > 0 && (
              <div className="overflow-x-auto border rounded-lg" data-testid="mgmt-type-stamp-restamp-samples">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-2 font-medium">Sample booking (stamp-only)</th>
                      <th className="text-left px-3 py-2 font-medium">Entity</th>
                      <th className="text-left px-3 py-2 font-medium">Platform</th>
                      <th className="text-left px-3 py-2 font-medium">Stored</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.stamp_restamp_samples.slice(0, 10).map((c, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-3 py-2 font-mono text-xs">{c.booking_id}</td>
                        <td className="px-3 py-2 text-xs">{c.entity_name}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{c.platform}</Badge></td>
                        <td className="px-3 py-2 text-xs">{c.stored_management_type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {preview.candidates.length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-2 font-medium">Booking</th>
                      <th className="text-left px-3 py-2 font-medium">Entity</th>
                      <th className="text-left px-3 py-2 font-medium">Platform</th>
                      <th className="text-left px-3 py-2 font-medium">Stored</th>
                      <th className="text-left px-3 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.candidates.slice(0, MAX_DISPLAY_ROWS).map((c, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-3 py-2 font-mono text-xs">{c.booking_id}</td>
                        <td className="px-3 py-2 text-xs">{c.entity_name}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{c.platform}</Badge></td>
                        <td className="px-3 py-2 text-xs">{c.stored_management_type}</td>
                        <td className="px-3 py-2">
                          <Badge variant={c.action === 'restamp' ? 'default' : 'secondary'} className="text-[10px]">
                            {c.action}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {applyResult && (
          <div className="space-y-1" data-testid="mgmt-type-backfill-apply-results">
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              Restamped {applyResult.total.restamped}, skipped (raced) {applyResult.total.skipped_raced}, stamp-only
              restamped {applyResult.total.stamp_restamped} (skipped raced {applyResult.total.stamp_skipped_raced}),
              needs resync {applyResult.total.needs_resync}.
            </div>
          </div>
        )}

        <div className="border-t pt-4 space-y-3">
          <div>
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Pencil className="h-4 w-4 text-blue-500" />
              Contradiction census: property says not managed, statements say it is
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              Starts from the opposite end of the backlog above: a property whose OWN
              management_type isn&apos;t &quot;managed&quot; but which has real CWC-style
              management statements against it (e.g. hostaway 564966, &quot;Comfy Place off
              Royal Mile&quot;, stuck at owned for months despite statements reconciling every
              month). Flip it here -- this edits management_type only, does not require
              re-assigning the entity, and survives the next platform sync. Its bookings then
              show up as a fresh mismatch in the restamp backlog above on next run.
            </p>
          </div>

          <Button
            onClick={runContradictionCensus}
            disabled={loadingContradiction}
            variant="outline"
            data-testid="mgmt-type-contradiction-census-btn"
          >
            {loadingContradiction ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Run Contradiction Census
          </Button>

          {contradiction && (
            <div className="space-y-2" data-testid="mgmt-type-contradiction-results">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Contradictions</p>
                  <p className="text-xl font-bold tabular-nums">{contradiction.total_contradictions}</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Unresolved property_ref</p>
                  <p className="text-xl font-bold tabular-nums text-amber-600 dark:text-amber-400">
                    {contradiction.unresolved_property_ref_count}
                  </p>
                </div>
              </div>

              {contradiction.total_contradictions === 0 ? (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  No property with real management statements is stamped as not-managed.
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Property</th>
                        <th className="text-left px-3 py-2 font-medium">Platform IDs</th>
                        <th className="text-left px-3 py-2 font-medium">Entity</th>
                        <th className="text-right px-3 py-2 font-medium">Statements</th>
                        <th className="text-left px-3 py-2 font-medium">Months</th>
                        <th className="text-left px-3 py-2 font-medium">Current</th>
                        <th className="text-left px-3 py-2 font-medium">Set to Managed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contradiction.rows.slice(0, MAX_DISPLAY_ROWS).map((r) => (
                        <ContradictionRow
                          key={r.canonical_property_id}
                          row={r}
                          saving={savingRowId === r.canonical_property_id}
                          onFlip={flipToManaged}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t pt-4 space-y-3">
          <div>
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Entity-blindness tally: entities neither census above can see
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              The restamp backlog above only walks properties already flagged managed; the
              contradiction census only walks properties with a real statement. An entity
              with ZERO of both, but a nonzero direct receivable total, is invisible to
              both -- exactly the shape ECI was in before its reclassification.
            </p>
          </div>

          <Button
            onClick={runBlindnessTally}
            disabled={loadingBlindness}
            variant="outline"
            data-testid="entity-blindness-tally-btn"
          >
            {loadingBlindness ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Run Entity-Blindness Tally
          </Button>

          {blindness && (
            <div className="space-y-2" data-testid="entity-blindness-tally-results">
              <div className="flex gap-3">
                <div className="rounded-lg border p-3 w-fit">
                  <p className="text-xs text-muted-foreground">Blind entities</p>
                  <p className="text-xl font-bold tabular-nums text-amber-600 dark:text-amber-400">
                    {blindness.blind_entity_count}
                  </p>
                </div>
                <div className="rounded-lg border p-3 w-fit" data-testid="entity-blindness-ruled-count">
                  <p className="text-xs text-muted-foreground">Direct (ruled)</p>
                  <p className="text-xl font-bold tabular-nums text-slate-600 dark:text-slate-400">
                    {ruledDirectCount}
                  </p>
                </div>
              </div>

              {blindness.blind_entity_count === 0 && ruledDirectCount === 0 ? (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  No entity is invisible to both censuses.
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Entity</th>
                        <th className="text-right px-3 py-2 font-medium">Managed properties</th>
                        <th className="text-right px-3 py-2 font-medium">Statements</th>
                        <th className="text-right px-3 py-2 font-medium">Direct receivable</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blindness.rows.map((r) => (
                        <tr key={r.entity_id} className="border-b last:border-0">
                          <td className="px-3 py-2">{r.entity_name}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.managed_property_count}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.statement_count}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {r.direct_receivable_total_native} {r.currency}
                          </td>
                          <td className="px-3 py-2">
                            {(r.label || (r.blind ? 'blind' : 'ok')) === 'blind' ? (
                              <Badge className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                                blind
                              </Badge>
                            ) : (r.label || (r.blind ? 'blind' : 'ok')) === 'direct (ruled)' ? (
                              <Badge className="text-[10px] bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                direct (ruled)
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px]">visible</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ContradictionRow({ row, saving, onFlip }) {
  const [feeRatePct, setFeeRatePct] = useState(20);

  return (
    <tr className="border-b last:border-0">
      <td className="px-3 py-2">{row.property_name}</td>
      <td className="px-3 py-2 font-mono text-xs">
        {Object.entries(row.platform_ids).map(([platform, ids]) => (
          <div key={platform}>{platform}: {ids.join(', ')}</div>
        ))}
      </td>
      <td className="px-3 py-2 text-xs">{row.entity_name}</td>
      <td className="px-3 py-2 text-right tabular-nums font-semibold">{row.statement_count}</td>
      <td className="px-3 py-2 text-xs">{row.months_covered.join(', ')}</td>
      <td className="px-3 py-2">
        <Badge variant="outline" className="text-[10px]">{row.stored_management_type}</Badge>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <input
            type="number"
            className="w-14 h-8 text-xs border rounded px-1 text-center"
            value={feeRatePct}
            min={1}
            max={100}
            disabled={saving}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val)) setFeeRatePct(val);
            }}
          />
          <span className="text-xs text-muted-foreground">%</span>
          <Button
            size="sm"
            disabled={saving}
            onClick={() => onFlip(row, feeRatePct / 100)}
            data-testid={`mgmt-type-contradiction-flip-${row.canonical_property_id}`}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Mark Managed'}
          </Button>
        </div>
      </td>
    </tr>
  );
}
